import type { FC } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { SectionLabel } from '@/components/ui/section-label';
import { useTranslation } from '@/hooks/useTranslation';
import type { MLMachineSummary, MLProcessSummary, MLToolSummary, MLCuttingParams } from '../type/MLTypes';
import { MLMachineSelect } from './MLMachineSelect';
import { MLSignalSourceStep } from './MLSignalSourceStep';
import type { SignalSource } from '@/features/signals/type/SignalsTypes';
import { MLProcessSelect } from './MLProcessSelect';
import { MLToolPicker } from './MLToolPicker';
import { MLRunButton } from './MLRunButton';
import { MLCuttingParamsStep } from './MLCuttingParamsStep';

interface MLContextPanelProps {
  machines: MLMachineSummary[];
  processes: MLProcessSummary[];
  tools: MLToolSummary[];
  machinesLoading: boolean;
  processesLoading: boolean;
  toolsLoading: boolean;
  selectedMachineId: number | null;
  selectedProcessId: number | null;
  selectedToolId: string | null;
  onMachineChange: (id: number) => void;
  onProcessChange: (id: number) => void;
  onToolChange: (toolId: string) => void;
  onReset: () => void;
  inferenceStatus: 'idle' | 'running' | 'completed' | 'failed';
  onRun: () => void;
  onCancel: () => void;
  cuttingParams: MLCuttingParams;
  onCuttingParamChange: (key: keyof MLCuttingParams, value: number) => void;
  onCuttingParamsReset: () => void;
  isParamsFromProcess: boolean;
  isParamsUserEdited: boolean;
  /** The selected machine's signal source (Workshop → Machines), null when it has none. */
  signalSource: SignalSource | null;
  signalSourceStatusKnown: boolean;
}

export const MLContextPanel: FC<MLContextPanelProps> = ({
  machines,
  processes,
  tools,
  machinesLoading,
  processesLoading,
  toolsLoading,
  selectedMachineId,
  selectedProcessId,
  selectedToolId,
  onMachineChange,
  onProcessChange,
  onToolChange,
  onReset,
  inferenceStatus,
  onRun,
  onCancel,
  cuttingParams,
  onCuttingParamChange,
  onCuttingParamsReset,
  isParamsFromProcess,
  isParamsUserEdited,
  signalSource,
  signalSourceStatusKnown,
}) => {
  const { t } = useTranslation();

  const isRunning = inferenceStatus === 'running';
  const hasAnySelection =
    selectedMachineId !== null || selectedProcessId !== null || selectedToolId !== null;
  const needsSignalSource = selectedMachineId !== null && signalSource === null;
  const canRun =
    selectedMachineId !== null &&
    selectedProcessId !== null &&
    selectedToolId !== null &&
    signalSource !== null;

  return (
    <Card className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <SectionLabel>{t('mlRul.context.badge')}</SectionLabel>
        {hasAnySelection && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            data-testid="reset-button"
          >
            {t('mlRul.context.reset')}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <MLMachineSelect
          machines={machines}
          value={selectedMachineId}
          onChange={onMachineChange}
          disabled={isRunning || machinesLoading}
        />

        <MLSignalSourceStep
          machineSelected={selectedMachineId !== null}
          source={signalSource}
          statusKnown={signalSourceStatusKnown}
        />

        <MLProcessSelect
          processes={processes}
          machineId={selectedMachineId}
          value={selectedProcessId}
          onChange={onProcessChange}
          disabled={isRunning || processesLoading}
        />

        {selectedProcessId !== null && (
          <MLToolPicker
            tools={tools}
            processId={selectedProcessId}
            value={selectedToolId}
            onChange={onToolChange}
            disabled={isRunning || toolsLoading}
          />
        )}

        {selectedToolId !== null && (
          <MLCuttingParamsStep
            params={cuttingParams}
            isFromProcess={isParamsFromProcess}
            isUserEdited={isParamsUserEdited}
            onParamChange={onCuttingParamChange}
            onResetToDefaults={onCuttingParamsReset}
            disabled={isRunning}
          />
        )}
      </div>

      <MLRunButton
        status={inferenceStatus}
        disabled={!canRun}
        onRun={onRun}
        onCancel={onCancel}
      />
      {needsSignalSource && (
        <p className="-mt-3 text-center text-xs text-muted-foreground" data-testid="run-needs-signal-source">
          {t('mlRul.context.runNeedsSignalSource')}
        </p>
      )}
    </Card>
  );
};
